/*
  Cart drawer controller (v4.1, 2026-08-06). See theme/CLAUDE.md.

  Adds over v3.9 (fixes the observed "second removal comes back / removes look dead" bug):
  - ROOT CAUSE (observed live on the V6.6 preview): a removal's /cart/change succeeded
    server-side, but when the fresh section swap didn't take (render hiccup, non-JSON 200
    body, or the silent repair refresh itself failing — third-party fetch wrappers from
    Easy Gift / web pixels / gtag were seen throwing "Failed to fetch"), the unconditional
    `itemEl.classList.remove('cart-line--removing')` cleanup UN-HID the old row. The
    server no longer had the line, so every later trash-click on that zombie row 404'd,
    was correctly judged "already applied", and silently did nothing — removal "worked
    once then stopped".
  - Success-path cleanup is now connectivity-aware: if the row node is still in the
    document after the section swap (i.e. the swap did NOT replace the list), a removal's
    row is dropped from the DOM instead of un-hidden. Same guard on the failure path,
    which now only un-hides the row when the change genuinely did not apply.
  - refreshCartNotification() is no longer a single point of silent failure: it retries
    once (800ms), treats cart.js as optional (sections alone still render), sends
    cache:'no-store', and as a last resort surgically removes a known-removed row by key
    so the drawer can never keep showing a line the server has already deleted.
  - Non-JSON 200 bodies from /cart/change (e.g. a bot-protection interstitial) no longer
    crash the success path: they route through the verify-against-cart.js recovery
    instead of JSON.parse throwing into the generic failure catch.

  Adds over v3.8:
  - Remove-with-undo toast shortened 5s -> 2s (shopper feedback: removal felt slow).
  - Render steps after a SUCCESSFUL request can no longer masquerade as request
    failures: success-path renders are try/caught (silent refresh repair), each
    section swap is isolated, and renderContents (add-to-cart) is hardened the same
    way. customElements.define calls are guarded against duplicate registration.
  - Failed /cart/change no longer alarms the shopper blindly: the server cart is
    re-checked first, and the generic banner only shows when the cart truly does NOT
    reflect the requested change (fixes the brief spurious "Something went wrong" flash
    seen right after a removal committed). Key-removals that genuinely failed get one
    silent retry via /cart/update.js before the banner.

  Adds over v3.7:
  - Quantity cap of 20/line with a numberless "Maximum quantity reached" notice; inventory
    rejections below the cap surface the same generic error (never a stock number, brief §4/§9).
  - Failed cart requests show a visible generic error banner and re-sync from the server so the
    drawer never silently desyncs (brief §9).
  - Remove-with-undo (brief §4): the row dims immediately and a "removed · UNDO" toast shows;
    the actual /cart/change removal is DEFERRED until the toast expires (keyed by line key, so it
    targets the right line even if indexes shift). Undo cancels before anything is sent. The
    pending removal survives AJAX re-renders (the row is re-hidden by key) and is COMMITTED
    before checkout, so the shopper is never charged for a line the drawer shows as removed.
  - Free-gift offer (settings.cart_gift_enabled): the data-gift-add banner button adds the
    picked gift variant (a Shopify automatic discount is what makes it $0) or just refreshes
    if the discount auto-added it already; data-gift-nav arrows page the choices carousel.
  - Checkout button spinner + disable (brief §8) without blocking the submit; state is restored
    on bfcache back-navigation via pageshow.
  - Analytics (brief §11): cart_drawer_open, checkout_click, milestone_unlocked, free_gift_add.
    (upsell_impression / upsell_add are fired by the upsell controller in the drawer shell.)
  NOTE: the order-summary card + in-cart discount entry (brief §7) were CUT from v3.8 in favour
  of the footer rows; the unused files are archived under theme/Cart files/.
*/
const CART_QTY_CAP = 20;
const CART_GENERIC_ERROR = 'Something went wrong updating your cart — please try again.';

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartNotification = this.closest('cart-notification');
      if (!cartNotification) return;
      const item = this.closest('.cart-notification-productItem');
      const key = this.dataset.key;
      if (key) {
        cartNotification.requestRemove(key, this.dataset.index, item);
      } else {
        if (item) item.classList.add('is-removing');
        cartNotification.updateQuantity(this.dataset.index, 0, null, item);
      }
    });
  }
}
// Guarded: assets/cart.js registers its own 'cart-remove-button' for the cart PAGE.
// If that file ever loads first, an unguarded define here throws and kills this whole
// file — the drawer then looks completely dead (no removes, no upsell adds).
if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

class CartNotification extends HTMLElement {
  constructor() {
    super();
    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.pendingRemove = null; // { key, name, itemEl, timer }
    this._prevHits = null;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('.cart-notification__close').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    document.querySelector('.PageOverlay').addEventListener('click', this.close.bind(this));
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value), 0
    );
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);
    this.addEventListener('change', this.debouncedOnChange.bind(this));

    // Delegated handlers that survive AJAX re-renders (undo, free gift, checkout).
    this.notification.addEventListener('click', this.handleDelegatedClick.bind(this));
    // Restore the checkout button when the page comes back from the bfcache (Back from checkout).
    window.addEventListener('pageshow', () => this.resetCheckoutButton());
    this._prevHits = this.countMilestoneHits();
  }

  /* ------------------------------ analytics ------------------------------ */
  emit(name, detail) {
    try { if (window.dataLayer) window.dataLayer.push(Object.assign({ event: name }, detail || {})); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('cart:' + name, { detail: detail || {} })); } catch (e) {}
  }
  countMilestoneHits() {
    return this.notification ? this.notification.querySelectorAll('.cart-reward__dot.is-hit').length : 0;
  }
  detectMilestoneUnlock() {
    const hits = this.countMilestoneHits();
    if (this._prevHits != null && hits > this._prevHits) this.emit('milestone_unlocked', { hits });
    this._prevHits = hits;
  }

  /* ------------------------------ open/close ----------------------------- */
  onChange(event) {
    // Only the quantity steppers (name="updates[]") drive cart updates. Other form
    // controls in the drawer bubble change events too — e.g. the variant dropdowns
    // (data-variant-select) — and must NOT trigger a /cart/change (the variant id
    // ended up sent as a quantity: request failed, error banner showed, and the
    // re-render reset the dropdown).
    if (event.target.name !== 'updates[]') return;
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }
  setEmptyState(itemCount) {
    if (!this.notification || typeof itemCount !== 'number') return;
    this.notification.classList.toggle('is-empty', itemCount === 0);
  }
  open(source) {
    this.notification.classList.add('animate', 'active');
    document.dispatchEvent(new CustomEvent('cart:open'));
    this.emit('cart_drawer_open', { source: source || 'unknown' });
    document.querySelector('.PageOverlay').classList.add('is-visible');
    document.body.classList.add('overflow-hidden');
    this.notification.addEventListener('transitionend', () => {
      this.notification.focus();
      trapFocus(this.notification);
    }, { once: true });
    document.body.addEventListener('click', this.onBodyClick);
  }
  close() {
    this.flushPendingRemove();
    this.notification.classList.remove('active');
    document.querySelector('.PageOverlay').classList.remove('is-visible');
    document.body.classList.remove('overflow-hidden');
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);
  }
  renderContents(parsedState) {
    // Add-to-cart path (product pages + in-drawer upsell cards). The add itself has
    // already succeeded when this runs, so any render/reveal hiccup must not abort
    // the flow silently (that's "the upsell add did nothing") — fall back to a full
    // silent re-sync and still open the drawer.
    this.productId = parsedState.id;
    try {
      this.renderSectionUpdates(parsedState);
      if (this.header) this.header.reveal();
      this.detectMilestoneUnlock();
    } catch (e) {
      this.refreshCartNotification();
    }
    if (this.notification) this.notification.classList.remove('is-empty');
    this.open('add');
  }
  renderSectionUpdates(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      // Per-section isolation: one bad section swap (e.g. an app-injected element whose
      // upgrade misbehaves) must not abort the remaining sections — a half-rendered
      // drawer is what made later removes/adds look dead.
      try {
        const container = document.getElementById(section.id);
        if (container && parsedState.sections && parsedState.sections[section.id]) {
          container.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      } catch (e) { /* keep going */ }
    });
    this.restorePendingRemoveState();
  }
  // A pending (undoable) removal is hidden only by a class on its row, and the server still
  // has the line until the toast expires — so any re-render rebuilds the row visible. Re-hide
  // it by key and re-point pendingRemove at the fresh node so Undo keeps working.
  restorePendingRemoveState() {
    const p = this.pendingRemove;
    if (!p || !this.notification) return;
    const row = this.notification.querySelector(`.cart-notification-productItem[data-key="${p.key}"]`);
    if (row) {
      row.classList.add('cart-line--removing');
      p.itemEl = row;
    }
  }
  // ⚠ Shopify's Section Rendering API returns NOTHING when 6+ sections are requested
  // (hard limit of 5). These five ARE the whole render contract — product-form.js reuses
  // this list for add-to-cart, which is how the header bubble (#cart-icon-bubble) stays
  // fresh. If a 6th live region is ever needed, fold it into an existing section.
  getSectionsToRender() {
    return [
      { id: 'cart-notification-product', selector: '.shopify-section' },
      { id: 'cart-notification-free-ship', selector: '.shopify-section' },
      { id: 'cart-notification-subtotal', selector: '.shopify-section' },
      { id: 'cart-upsell', selector: '.shopify-section' },
      { id: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }

  /* ------------------------------ error banner --------------------------- */
  showError(message) {
    const el = this.notification && this.notification.querySelector('[data-cart-error]');
    if (!el) return;
    el.textContent = message || CART_GENERIC_ERROR;
    el.classList.add('is-visible');
    clearTimeout(this._errTimer);
    this._errTimer = setTimeout(() => el.classList.remove('is-visible'), 4000);
  }
  hideError() {
    const el = this.notification && this.notification.querySelector('[data-cart-error]');
    if (el) el.classList.remove('is-visible');
  }

  /* ------------------------------ quantity ------------------------------- */
  // Shared /cart/change pipeline for the quantity path ({line}) and the remove/undo path
  // ({id}) — one copy so the two flows can never drift. afterRender runs between hideError
  // and the checkout re-enable, matching the original ordering of the per-path notices.
  sendCartChange(payloadFields, itemEl, afterRender) {
    const checkoutBtn = document.querySelector('button[name="checkout"]');
    if (checkoutBtn) checkoutBtn.setAttribute('disabled', '');
    const body = JSON.stringify({
      ...payloadFields,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname
    });
    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => {
        if (!response.ok) throw new Error('cart change failed');
        return response.text();
      })
      .then((state) => {
        // A 200 whose body isn't clean JSON (e.g. a bot-protection interstitial) must
        // not fall into the generic failure catch — the change very likely applied.
        // Route it through the verify-against-cart.js recovery instead.
        let parsedState = null;
        try { parsedState = JSON.parse(state); } catch (e) { parsedState = null; }
        if (!parsedState) {
          if (checkoutBtn) checkoutBtn.removeAttribute('disabled');
          this.recoverFailedChange(payloadFields, itemEl);
          return;
        }
        // The request SUCCEEDED at this point. A hiccup while re-rendering must never
        // be treated as a failed cart change (that path shows the error banner and
        // re-syncs — the spurious-error bug) — repair with a silent refresh instead.
        const removeKey = payloadFields.id != null && payloadFields.quantity === 0 ? payloadFields.id : null;
        try {
          this.renderSectionUpdates(parsedState);
          this.setEmptyState(parsedState.item_count);
          this.detectMilestoneUnlock();
          if (afterRender) afterRender(parsedState);
        } catch (e) {
          this.refreshCartNotification({ removeKey });
        }
        this.hideError();
        if (checkoutBtn) checkoutBtn.removeAttribute('disabled');
        // Connectivity-aware cleanup. After a successful section swap the old row node is
        // DETACHED, so stripping classes is a harmless no-op. If the node is still in the
        // document, the swap did NOT replace the list — un-hiding a removed line here is
        // exactly the "item came back" zombie (observed live). Drop it instead.
        if (itemEl) {
          if (itemEl.isConnected && removeKey) {
            itemEl.remove();
          } else {
            itemEl.classList.remove('is-removing', 'cart-line--removing');
          }
        }
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(() => {
        if (checkoutBtn) checkoutBtn.removeAttribute('disabled');
        // Don't un-hide the row yet: whether it should reappear depends on whether the
        // change actually applied, which recoverFailedChange determines.
        this.recoverFailedChange(payloadFields, itemEl);
      });
  }
  // A failed /cart/change is not always a failed CHANGE: the server may have applied it
  // anyway (or a later re-render step threw after it applied). Blindly showing the error
  // banner here produced a spurious "Something went wrong" flash right after removals
  // even though the cart was correct. So: re-read cart.js, and only alarm the shopper
  // when the server cart truly does NOT reflect what was requested. Either way we
  // re-sync the drawer so it never desyncs (brief §9).
  recoverFailedChange(payloadFields, itemEl) {
    const removeKey = payloadFields && payloadFields.id != null && payloadFields.quantity === 0 ? payloadFields.id : null;
    // Only genuine not-applied outcomes should un-hide the row (so the shopper can retry).
    // When the change DID apply, un-hiding would resurrect a line the server no longer has.
    const restoreRow = () => { if (itemEl) itemEl.classList.remove('is-removing', 'cart-line--removing'); };
    fetch(`${window.Shopify.routes.root}cart.js`, { cache: 'no-store' })
      .then((r) => { if (!r.ok) throw new Error('cart fetch failed'); return r.json(); })
      .then((cart) => {
        if (this.changeWasApplied(payloadFields, cart)) {
          this.refreshCartNotification({ removeKey }); // change actually took — silent re-sync, no banner
          return;
        }
        // A remove-by-key that genuinely failed gets ONE silent retry through
        // /cart/update.js (which also accepts line keys) before we alarm the shopper.
        if (removeKey != null) {
          const updates = {};
          updates[removeKey] = 0;
          fetch(`${window.Shopify.routes.root}cart/update.js`, { ...fetchConfig(), body: JSON.stringify({ updates }) })
            .then((r) => { if (!r.ok) throw new Error('retry failed'); })
            .then(() => { this.hideError(); this.refreshCartNotification({ removeKey }); })
            .catch(() => { restoreRow(); this.showError(CART_GENERIC_ERROR); this.refreshCartNotification(); });
          return;
        }
        restoreRow();
        this.showError(CART_GENERIC_ERROR);
        this.refreshCartNotification();
      })
      .catch(() => {
        // cart.js unreachable — can't verify either way. Err on the side of the shopper
        // being able to retry: restore the row and re-sync.
        restoreRow();
        this.showError(CART_GENERIC_ERROR);
        this.refreshCartNotification();
      });
  }
  // Does the server cart already reflect this /cart/change payload? Only the key-addressed
  // path ({id}) can be verified with certainty; the line-number path returns false so it
  // keeps the old always-show-error behaviour.
  changeWasApplied(payloadFields, cart) {
    if (payloadFields == null || cart == null) return false;
    const qty = parseInt(payloadFields.quantity, 10);
    if (payloadFields.id != null) {
      const item = (cart.items || []).find((i) => i.key === payloadFields.id);
      if (qty === 0) return !item;
      return !!item && item.quantity === qty;
    }
    return false;
  }
  updateQuantity(line, quantity, name, itemEl = null) {
    let requested = parseInt(quantity, 10);
    if (isNaN(requested)) return;
    let capped = false;
    if (requested > CART_QTY_CAP) { requested = CART_QTY_CAP; capped = true; }
    this.sendCartChange({ line, quantity: requested }, itemEl, (parsedState) => {
      if (capped) {
        this.showError('Maximum quantity reached');
      } else if (requested > 0) {
        const li = parsedState.items && parsedState.items[parseInt(line, 10) - 1];
        if (li && li.quantity < requested) this.showError(CART_GENERIC_ERROR);
      }
    });
  }

  /* -------------------------- remove with undo --------------------------- */
  requestRemove(key, index, itemEl) {
    this.flushPendingRemove(); // only one pending removal at a time
    const nameEl = itemEl && itemEl.querySelector('.cart-notification-product__name');
    const name = nameEl ? nameEl.textContent.trim() : 'Item';
    if (itemEl) itemEl.classList.add('cart-line--removing');
    // 2s: long enough to hit UNDO, short enough that the removal doesn't feel stuck
    // (was 5s, then 3s — Daniel asked for 2s).
    const timer = setTimeout(() => this.commitPendingRemove(), 1000);
    this.pendingRemove = { key, index, name, itemEl, timer };
    this.showUndoToast(name);
  }
  commitPendingRemove() {
    const p = this.pendingRemove;
    if (!p) return;
    this.pendingRemove = null;
    this.hideUndoToast();
    this.changeById(p.key, 0, p.itemEl);
  }
  flushPendingRemove() {
    if (this.pendingRemove) {
      clearTimeout(this.pendingRemove.timer);
      this.commitPendingRemove();
    }
  }
  undoRemove() {
    const p = this.pendingRemove;
    if (!p) return;
    clearTimeout(p.timer);
    if (p.itemEl) p.itemEl.classList.remove('cart-line--removing');
    this.pendingRemove = null;
    this.hideUndoToast();
  }
  showUndoToast(name) {
    const toast = this.notification.querySelector('[data-undo-toast]');
    if (!toast) return;
    const text = toast.querySelector('[data-undo-text]');
    if (text) text.textContent = `${name} removed`;
    toast.classList.add('is-visible');
  }
  hideUndoToast() {
    const toast = this.notification.querySelector('[data-undo-toast]');
    if (toast) toast.classList.remove('is-visible');
  }
  // Change a line by its Shopify line key (stable across index shifts).
  changeById(id, quantity, itemEl) {
    return this.sendCartChange({ id, quantity }, itemEl, null);
  }

  /* ----------------------------- free gift -------------------------------- */
  // "Add free" on the gift-offer banner (snippets/cart-gift-offer.liquid). A Shopify
  // automatic discount is what makes the gift $0 — and if that discount auto-added the
  // gift already, adding again would duplicate it, so check the cart first and just
  // refresh in that case.
  addFreeGift(btn) {
    btn.setAttribute('disabled', '');
    btn.setAttribute('aria-busy', 'true');
    const variantId = parseInt(btn.dataset.variantId, 10);
    // The duplicate pre-check is best-effort: the banner only renders when the gift is NOT
    // in the cart, so if cart.js is unreachable (e.g. a bot-protection interstitial) we
    // still proceed with the add rather than failing the whole action.
    fetch(`${window.Shopify.routes.root}cart.js`)
      .then((r) => r.json())
      .catch(() => null)
      .then((cart) => {
        const alreadyInCart = cart && cart.items.some((item) => item.variant_id === variantId);
        if (alreadyInCart) return null;
        const body = JSON.stringify({ items: [{ id: variantId, quantity: 1 }] });
        return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
          .then((response) => { if (!response.ok) throw new Error('gift add failed'); });
      })
      .then(() => {
        this.emit('free_gift_add', { variant: variantId });
        this.refreshCartNotification(); // re-render removes the banner and shows the gift line
      })
      .catch(() => {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-busy');
        this.showError(CART_GENERIC_ERROR);
        this.refreshCartNotification();
      });
  }

  /* --------------------------- delegated clicks -------------------------- */
  handleDelegatedClick(evt) {
    const undoBtn = evt.target.closest('[data-undo-btn]');
    if (undoBtn) { evt.preventDefault(); this.undoRemove(); return; }
    const giftBtn = evt.target.closest('[data-gift-add]');
    if (giftBtn) { evt.preventDefault(); this.addFreeGift(giftBtn); return; }
    const giftNav = evt.target.closest('[data-gift-nav]');
    if (giftNav) {
      evt.preventDefault();
      const track = this.notification.querySelector('[data-gift-track]');
      if (track) {
        const dir = giftNav.dataset.giftNav === 'prev' ? -1 : 1;
        const w = track.clientWidth;
        const target = Math.max(0, Math.min(track.scrollWidth - w, Math.round(track.scrollLeft / w) * w + dir * w));
        track.scrollTo({ left: target, behavior: 'smooth' });
        // Programmatic smooth scrolling gets cancelled on this snap container in some
        // browsers/theme-script combos — verify it moved and jump instantly if not.
        setTimeout(() => {
          if (Math.abs(track.scrollLeft - target) > 2) track.scrollLeft = target;
        }, 400);
      }
      return;
    }
    const checkoutBtn = evt.target.closest('button[name="checkout"]');
    if (checkoutBtn && !checkoutBtn.disabled) {
      this.emit('checkout_click', {});
      if (this.pendingRemove) {
        // Commit the deferred removal BEFORE checkout — letting the native submit run
        // would navigate away with the "removed" line still in the server cart.
        evt.preventDefault();
        const p = this.pendingRemove;
        clearTimeout(p.timer);
        this.pendingRemove = null;
        this.hideUndoToast();
        this.setCheckoutBusy(checkoutBtn);
        fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body: JSON.stringify({ id: p.key, quantity: 0 }) })
          .then((response) => {
            if (!response.ok) throw new Error('cart change failed');
            this.submitCheckout(checkoutBtn);
          })
          .catch(() => {
            // Same false-negative guard as sendCartChange: if the server cart shows the
            // line already gone, the removal DID take — proceed to checkout instead of
            // stranding the shopper behind a spurious error.
            fetch(`${window.Shopify.routes.root}cart.js`)
              .then((r) => { if (!r.ok) throw new Error('cart fetch failed'); return r.json(); })
              .then((cart) => {
                if (this.changeWasApplied({ id: p.key, quantity: 0 }, cart)) {
                  this.submitCheckout(checkoutBtn);
                } else {
                  this.resetCheckoutButton();
                  this.showError(CART_GENERIC_ERROR);
                  this.refreshCartNotification();
                }
              })
              .catch(() => {
                this.resetCheckoutButton();
                this.showError(CART_GENERIC_ERROR);
                this.refreshCartNotification();
              });
          });
        return;
      }
      // Swap to a spinner + disable without blocking the native submit (after the tick).
      setTimeout(() => this.setCheckoutBusy(checkoutBtn), 0);
    }
  }
  // Re-trigger the checkout POST after an async pre-step. form.submit() does not include
  // the submit button's name=checkout pair (which is what makes /cart redirect to
  // checkout), so carry it via a hidden input.
  submitCheckout(checkoutBtn) {
    const form = checkoutBtn.closest('form');
    if (!form) {
      window.location.href = `${window.Shopify.routes.root}checkout`;
      return;
    }
    if (!form.querySelector('input[name="checkout"]')) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      hidden.value = '';
      form.appendChild(hidden);
    }
    form.submit();
  }
  setCheckoutBusy(btn) {
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('disabled', '');
    const label = btn.querySelector('span');
    if (label) { this._checkoutLabel = label.textContent; label.textContent = ''; }
    if (!btn.querySelector('.cart-notification__checkout-spinner')) {
      const spin = document.createElement('span');
      spin.className = 'cart-notification__checkout-spinner';
      spin.setAttribute('role', 'status');
      spin.setAttribute('aria-label', 'Loading checkout');
      btn.appendChild(spin);
    }
  }
  resetCheckoutButton() {
    const btn = document.querySelector('button[name="checkout"]');
    if (!btn || btn.getAttribute('aria-busy') !== 'true') return;
    btn.removeAttribute('aria-busy');
    btn.removeAttribute('disabled');
    const spin = btn.querySelector('.cart-notification__checkout-spinner');
    if (spin) spin.remove();
    const label = btn.querySelector('span');
    if (label && this._checkoutLabel) label.textContent = this._checkoutLabel;
  }

  // The silent repair path for every hiccup — so it must not itself be a silent point of
  // failure (observed live: a third-party fetch-wrapper "Failed to fetch" here left the
  // drawer permanently stale). One retry; cart.js is optional (sections alone still
  // render); and if BOTH attempts fail after a known removal, the removed row is dropped
  // from the DOM directly so the drawer can never keep showing a deleted line.
  refreshCartNotification(opts, attempt = 0) {
    const root = window.Shopify.routes.root;
    const ids = this.getSectionsToRender().map((s) => s.id).join(',');
    // The two requests are independent — run them concurrently; cart.js failure alone
    // must not doom the section render.
    Promise.all([
      fetch(`${root}?sections=${ids}`, { cache: 'no-store' })
        .then((res) => { if (!res.ok) throw new Error('sections fetch failed'); return res.json(); }),
      fetch(`${root}cart.js`, { cache: 'no-store' })
        .then((r) => { if (!r.ok) throw new Error('cart fetch failed'); return r.json(); })
        .catch(() => null)
    ])
      .then(([sections, cart]) => {
        // renderSectionUpdates strips the .shopify-section wrapper the sections API
        // returns, keeping this path's DOM identical to the /cart/change path's.
        this.renderSectionUpdates({ sections });
        if (cart) this.setEmptyState(cart.item_count);
        this.detectMilestoneUnlock();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch((e) => {
        if (attempt < 1) {
          setTimeout(() => this.refreshCartNotification(opts, attempt + 1), 800);
          return;
        }
        if (opts && opts.removeKey && this.notification) {
          const row = this.notification.querySelector(`.cart-notification-productItem[data-key="${opts.removeKey}"]`);
          if (row) row.remove();
        }
        console.error(e);
      });
  }
  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : html;
  }
  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }
  setActiveElement(element) {
    this.activeElement = element;
  }
}
if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
